import { ref } from 'vue';
import { defineStore } from 'pinia';
import useAuthStore from '@/store/modules/auth.js';
import { useLocalStorageHook } from '@/hooks/usePersistentStorage.js';
import { languageStorageKey } from '@/constants/persistentStorage.js';
import { apiUpdateAuthUserLanguage } from '@/services/user.js';
import {
  apiRetrieveDefaultLanguage,
  apiRetrieveLanguages,
  apiRetrieveTranslations,
} from '@/services/locale.js';

const useLocaleStore = defineStore('locale', () => {
  const authStore = useAuthStore();

  const [currentLanguage, setCurrentLanguage] = useLocalStorageHook(languageStorageKey, '');

  const languages = ref({});
  const translations = ref({});

  const storeLanguages = (value) => {
    languages.value = value;
  };

  const storeTranslations = (value) => {
    translations.value = value;
  };

  const dispatchRetrieveDefaultLanguage = async () => {
    try {
      const { parsedData } = await apiRetrieveDefaultLanguage();

      if (!currentLanguage.value) {
        setCurrentLanguage(parsedData);
      }
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const dispatchRetrieveLanguages = async () => {
    try {
      const { parsedData, languageKeyList } = await apiRetrieveLanguages();

      storeLanguages(parsedData);

      if (!languageKeyList.includes(currentLanguage.value)) {
        setCurrentLanguage(languageKeyList[0]);
      }
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const dispatchRetrieveTranslations = async () => {
    try {
      const { parsedData } = await apiRetrieveTranslations({ lang: currentLanguage.value });

      storeTranslations(parsedData);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const changeLanguage = async (langKey) => {
    setCurrentLanguage(langKey);

    if (authStore.isUserAuthenticated) {
      await apiUpdateAuthUserLanguage({ language: currentLanguage.value });
    }

    window.location.reload();
  };

  return {
    currentLanguage,
    languages,
    translations,
    setCurrentLanguage,
    dispatchRetrieveDefaultLanguage,
    dispatchRetrieveLanguages,
    dispatchRetrieveTranslations,
    changeLanguage,
  };
});

export default useLocaleStore;
